import React from 'react';
import { AlertTriangle, Check, ChevronDown } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import type { Booking, WhatIfBookingReduction } from '@/types';
import {
  formatReductionDateRange,
  formatReductionDelta,
  formatReductionHours,
  formatReductionSpan,
  uniformDailyHours,
} from '@/utils/whatif-reduction';

export interface WhatIfReductionSummaryData {
  details: WhatIfBookingReduction[];
  beforeHours: number;
  afterHours: number;
  /** Hours asked for across every date of the reduced bookings. */
  targetHours: number;
  /** The same totals for the week the Reduce slider was set for. */
  week: { label: string; beforeHours: number; afterHours: number; targetHours: number } | null;
  /** At-risk resource's peak utilisation before / after the change. */
  beforePct: number | null;
  afterPct: number | null;
}

/** Proposed hours are rounded to 15 minutes per day, so allow that much drift. */
const TARGET_TOLERANCE_HOURS = 0.25;

interface WhatIfReductionSummaryProps {
  summary: WhatIfReductionSummaryData;
  bookingsById: Map<string, Booking>;
}

/**
 * Reduce-hours preview totals shown in the what-if banner: booked hours
 * before → after, whether that hits the chosen target, the resource's peak
 * utilisation, and a per-booking breakdown.
 */
export const WhatIfReductionSummary: React.FC<WhatIfReductionSummaryProps> = ({ summary, bookingsById }) => {
  const { details, beforeHours, afterHours, targetHours, week, beforePct, afterPct } = summary;
  const cut = beforeHours - afterHours;
  const matchesTarget = Math.abs(afterHours - targetHours) <= TARGET_TOLERANCE_HOURS * details.length;
  const dayKeys = details.flatMap((d) => d.days.map((day) => day.dateKey)).sort();
  const allDatesLabel = dayKeys.length > 0
    ? formatReductionDateRange(dayKeys[0], dayKeys[dayKeys.length - 1])
    : '';

  const targetBadge = matchesTarget ? (
    <Badge variant="success" className="gap-1 whitespace-nowrap">
      <Check className="size-3" aria-hidden="true" />
      {week ? `Matches ${formatReductionHours(week.targetHours)}/wk target` : 'Matches target'}
    </Badge>
  ) : (
    <Badge variant="warning" className="gap-1 whitespace-nowrap">
      <AlertTriangle className="size-3" aria-hidden="true" />
      Target {formatReductionHours(targetHours)} · actual {formatReductionHours(afterHours)}
    </Badge>
  );

  return (
    <div className="flex shrink-0 items-center gap-2">
      {week && (
        <Badge variant="outline" className="whitespace-nowrap bg-background">
          {week.label}: {formatReductionHours(week.beforeHours)} → {formatReductionHours(week.afterHours)}/wk
          <span className="ml-1 text-destructive">({formatReductionDelta(week.afterHours - week.beforeHours)})</span>
        </Badge>
      )}
      <Badge variant="outline" className="whitespace-nowrap bg-background" title={allDatesLabel}>
        All dates: {formatReductionHours(beforeHours)} → {formatReductionHours(afterHours)}
        <span className="ml-1 text-destructive">({formatReductionDelta(-cut)})</span>
      </Badge>
      {targetBadge}
      {beforePct != null && afterPct != null && (
        <Badge variant="outline" className="whitespace-nowrap bg-background">
          Peak {beforePct}% → {afterPct}%
        </Badge>
      )}
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-6 gap-1 px-2 text-xs">
            View breakdown
            <ChevronDown className="size-3" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[520px] p-0">
          <div className="border-b border-border px-4 py-3">
            <p className="text-sm font-semibold text-foreground">
              How the {formatReductionHours(cut)} reduction is split
            </p>
            <p className="text-xs text-muted-foreground">
              Totals cover every date of these bookings ({allDatesLabel}). Each day is cut by the same share.
            </p>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Booking</TableHead>
                <TableHead className="text-right">Before</TableHead>
                <TableHead className="text-right">After</TableHead>
                <TableHead className="text-right">Change</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {details.map((d) => {
                const booking = bookingsById.get(d.bookingId);
                const daily = uniformDailyHours(d);
                return (
                  <TableRow key={d.bookingId}>
                    <TableCell>
                      <div className="font-medium text-foreground">
                        {booking?.description || booking?.projectName || 'Booking'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {formatReductionSpan(d)}
                        {daily && ` · ${formatReductionHours(daily.before)} → ${formatReductionHours(daily.after)} a day`}
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">{formatReductionHours(d.beforeHours)}</TableCell>
                    <TableCell className="text-right font-semibold">{formatReductionHours(d.afterHours)}</TableCell>
                    <TableCell className="text-right font-semibold text-destructive">
                      {formatReductionDelta(d.afterHours - d.beforeHours)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="font-semibold">Total</TableCell>
                <TableCell className="text-right">{formatReductionHours(beforeHours)}</TableCell>
                <TableCell className="text-right font-semibold">{formatReductionHours(afterHours)}</TableCell>
                <TableCell className="text-right font-semibold text-destructive">{formatReductionDelta(-cut)}</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
          <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-2 text-xs text-muted-foreground">
            <span>
              {week
                ? `${formatReductionHours(week.targetHours)}/wk in ${week.label} = ${formatReductionHours(targetHours)} across all dates`
                : `Target ${formatReductionHours(targetHours)}`}
            </span>
            {targetBadge}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
